import React from 'react';
import PropTypes from 'prop-types';
import { isNotEmpty, isNumberInRange, isGreaterThanOrEqual } from '../../utils/validator';
import { errorMessageOf } from '../elements/formHook/errors';
import { useTerritory } from '../../hooks/useTerritory';
import { useTranslate } from '../../translate/I18n';

import Box from '../atoms/Box';
import Button from '../atoms/Button';
import Card from '../atoms/Card';
import LabelMessage from '../atoms/LabelMessage';
import Text from '../atoms/Text';
import { useForm, FormProvider } from 'react-hook-form';
import InputWithLabel from '../molecules/form/InputWithLabel';

const CopyrightCalculatorForm = ({ defaultValues, calculateCopyrightAdvantage }) => {
  const formHook = useForm({ defaultValues });
  const { handleSubmit } = formHook;
  const territory = useTerritory();
  const { t } = useTranslate(['validation', 'copyrightCalculator']);

  const calculate = handleSubmit(
    async ({ allowedTimeCreativeWork, annualGrossIncome }) =>
      await calculateCopyrightAdvantage(allowedTimeCreativeWork, annualGrossIncome)
  );

  return (
    <FormProvider {...formHook}>
      <Card
        width="100%"
        maxWidth={{ _: '360px', lg: '320px' }}
        px={{ _: 1, sm: 2, md: 4 }}
        py={3}
        mx="auto"
        mb={{ _: 2, lg: 0 }}>
        <Text as="h1" styleName="pH2" textAlign={{ _: 'center', lg: 'left' }}>
          {t('copyrightCalculator:authorsRightRegime')}
        </Text>

        <Text styleName="pPLight" textAlign={{ _: 'center', lg: 'left' }} mb={6}>
          {t('copyrightCalculator:howMuchYouSave')}
        </Text>
        <InputWithLabel
          defaultValue={defaultValues.allowedTimeCreativeWork}
          suffix="%"
          block={true}
          type="number"
          label={t('copyrightCalculator:allowedTimeCreativeWork')}
          errorMessage={errorMessageOf(t, 'allowedTimeCreativeWork', formHook.errors)}
          ref={formHook.register({
            validate: {
              fillCreativeWorkTime: isNotEmpty,
              timeCreativeWorkBadRange: isNumberInRange(0, 100)
            }
          })}
          name="allowedTimeCreativeWork"
          placeholder={territory.currencySymbol}
        />
        <Box mb={3} mt={'-18px'}>
          <LabelMessage withIcon={true} type="default">
            {t('copyrightCalculator:inPercentageOfTime')}
          </LabelMessage>
        </Box>

        <InputWithLabel
          defaultValue={defaultValues.annualGrossIncome}
          type="number"
          suffix={territory.currencySymbol}
          block={true}
          label={t('copyrightCalculator:annualGrossIncome')}
          errorMessage={errorMessageOf(t, 'annualGrossIncome', formHook.errors)}
          ref={formHook.register({
            validate: {
              fillGrossIncome: isNotEmpty,
              grossIncomeNegative: isGreaterThanOrEqual(0)
            }
          })}
          name="annualGrossIncome"
          placeholder={territory.currencySymbol}
        />

        <Button data-cy="btn-saveMoney" width="100%" styleType="secondary" mb={1} onClick={() => calculate()}>
          {t('copyrightCalculator:calculate')}
        </Button>
      </Card>
    </FormProvider>
  );
};

CopyrightCalculatorForm.propTypes = {
  calculateCopyrightAdvantage: PropTypes.func,
  defaultValues: PropTypes.shape({
    allowedTimeCreativeWork: PropTypes.number,
    annualGrossIncome: PropTypes.number
  })
};

export default CopyrightCalculatorForm;
